<template>
    <div class="px-6 py-5 overflow-y-auto right-content-grid-vs h-[calc(100vh-309px)]">
        <div class="w-full">
            <div v-if="isLoading" class="text-gray-600 font-sans py-4">
                <p>Loading data...</p>
            </div>
            <div
                v-else-if="!authStore.user?.organization_id"
                class="p-4 bg-yellow-50 font-sans border border-yellow-200 rounded-md mb-4"
            >
                <p class="text-amber-700">Unable to load organization data. Please try refreshing the page.</p>
                <button
                    @click="authStore.fetchUserData"
                    class="bg-amber-600 font-sans text-white px-4 py-1 rounded-md mt-2"
                >
                    Retry Loading Organization Data
                </button>
            </div>
            <div v-else class="text-[#414141] font-sans">
                <h3 class="text-[1.25rem] leading-[1.75rem] mb-[1rem] font-semibold">
                    Outcome Theme(s)
                </h3>
                <p class="text-[14px] leading-[22px] mb-[0.5rem]">What measurable changes do you ultimately hope to achieve in your community, field, or population as a result of your organization’s work?</p>
                <p class="text-[14px] leading-[22px] mb-[0.5rem]">Enter up to five (5) Outcome Theme(s) and a brief description (up to 85 words) for each one.</p>

                <div class="mt-6">
                    <h4 class="text-[1rem] leading-[1.5rem] mb-[0.5rem] font-semibold">Outcome Theme(s) <span class="text-[#6B7280]">(5 Outcome Theme(s) Max)</span></h4>

                    <!-- Outcomes Container -->
                    <div class="mt-4">
                        <!-- Outcome Item -->
                        <div
                            v-for="(outcome, idx) in outcomeState.outcomes"
                            :key="idx"
                            class="bg-[#F5F6FA] rounded-md overflow-hidden border border-[#00423D] mt-4"
                            :class="{'border-red-400 shakeX': !isOutcomesValid.isValid && (!outcome.title?.trim() || !outcome.description?.trim() || outcome.wordCount > 85)}"
                        >
                            <!-- Outcome Header -->
                            <div class="py-2 px-4 flex items-center justify-between gap-4 flex-wrap">
                                <div class="flex flex-1 items-center gap-2">
                                    <div class="flex flex-1 flex-col">
                                        <div class="text-sm text-[#00423D] mb-2">Outcome Theme</div>
                                        <input
                                            :id="'outcome-theme-' + idx"
                                            v-model="outcome.title"
                                            type="text"
                                            aria-label="Outcome Theme"
                                            class="w-full px-2 py-2 bg-transparent outline-none focus:outline-none transition-colors duration-200"
                                            placeholder="Write Outcome Theme here..."
                                            @input="onOutcomeThemeInput"
                                        />
                                    </div>
                                    <button class="p-1 self-end" @click="onRemoveOutcome" :data-point-id="idx">
                                        <svg class="w-6 h-6 pointer-events-none" width="49" height="49" viewBox="0 0 49 49" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <mask id="mask0_1675_29390" style="mask-type: alpha" maskUnits="userSpaceOnUse" x="0" y="0" width="49" height="49">
                                                <rect x="0.0527344" y="0.480957" width="48" height="48" fill="#D9D9D9"/>
                                            </mask>
                                            <g mask="url(#mask0_1675_29390)">
                                                <path d="M13.1027 42.481C12.2694 42.481 11.5611 42.1893 10.9777 41.606C10.3944 41.0226 10.1027 40.3143 10.1027 39.481V10.981H8.05273V7.98096H17.4527V6.48096H30.6527V7.98096H40.0527V10.981H38.0027V39.481C38.0027 40.281 37.7027 40.981 37.1027 41.581C36.5027 42.181 35.8027 42.481 35.0027 42.481H13.1027ZM35.0027 10.981H13.1027V39.481H35.0027V10.981ZM18.4027 35.181H21.4027V15.231H18.4027V35.181ZM26.7027 35.181H29.7027V15.231H26.7027V35.181Z" class="fill-[#6B7280]"/>
                                            </g>
                                        </svg>
                                    </button>
                                </div>
                            </div>

                            <!-- Outcome Description -->
                            <div class="px-4 py-3 border-t border-t-gray-200 bg-white">
                                <textarea
                                    :id="'outcome-description-' + idx"
                                    v-model="outcome.description"
                                    aria-label="Outcome Description"
                                    class="w-full px-2 py-2 bg-transparent outline-none focus:outline-none transition-colors duration-200 resize-none"
                                    rows="2"
                                    placeholder="Write Outcome Description here..."
                                    @input="onOutcomeDescriptionInput"
                                    :data-outcome-index="idx"
                                ></textarea>
                                <p :class="{ 'text-red-500': outcome.wordCount > 85 }" class="text-sm mt-2">
                                    {{ outcome.wordCount }} / 85 words
                                </p>
                            </div>
                        </div>

                        <!-- Validation Message -->
                        <transition name="answer-err">
                            <p ref="validationMessageRef" class="pl-2 mt-1.5 text-sm text-red-400" v-if="!isOutcomesValid.isValid && showValidationError">
                                {{ isOutcomesValid.issue === "empty"
                                    ? "Please Add At Least One Outcome!"
                                    : isOutcomesValid.issue === "duplicate"
                                        ? duplicateErrorMessage
                                        : "Make Sure To Include Outcome Theme And Description!"
                                }}
                            </p>
                        </transition>

                        <!-- Add Outcome Button -->
                        <button
                            v-if="outcomeState.outcomes.length < 5"
                            class="flex items-center text-white bg-[#00423D] py-2 px-4 rounded-lg mt-4 hover:bg-[#009C87] transition-colors duration-200"
                            @click="onAddOutcome"
                        >
                            <svg class="h-6 w-6 pointer-events-none" width="49" height="49" viewBox="0 0 49 49" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <mask id="mask0_1409_29353" style="mask-type: alpha" maskUnits="userSpaceOnUse" x="0" y="0" width="49" height="49">
                                    <rect x="0.322266" y="0.00683594" width="48" height="48" fill="#D9D9D9"/>
                                </mask>
                                <g mask="url(#mask0_1409_29353)">
                                    <path d="M24.3223 38.0068C23.8889 38.0068 23.5306 37.8652 23.2473 37.5818C22.9639 37.2985 22.8223 36.9402 22.8223 36.5068V25.5068H11.8223C11.3889 25.5068 11.0306 25.3652 10.7473 25.0818C10.4639 24.7985 10.3223 24.4402 10.3223 24.0068C10.3223 23.5735 10.4639 23.2152 10.7473 22.9318C11.0306 22.6485 11.3889 22.5068 11.8223 22.5068H22.8223V11.5068C22.8223 11.0735 22.9639 10.7152 23.2473 10.4318C23.5306 10.1485 23.8889 10.0068 24.3223 10.0068C24.7556 10.0068 25.1139 10.1485 25.3973 10.4318C25.6806 10.7152 25.8223 11.0735 25.8223 11.5068V22.5068H36.8223C37.2556 22.5068 37.6139 22.6485 37.8973 22.9318C38.1806 23.2152 38.3223 23.5735 38.3223 24.0068C38.3223 24.4402 38.1806 24.7985 37.8973 25.0818C37.6139 25.3652 37.2556 25.5068 36.8223 25.5068H25.8223V36.5068C25.8223 36.9402 25.6806 37.2985 25.3973 37.5818C25.1139 37.8652 24.7556 38.0068 24.3223 38.0068Z" class="fill-white"/>
                                </g>
                            </svg>
                            <span class="pointer-events-none ml-2">Add Outcome</span>
                        </button>
                    </div>
                </div>

                <p v-if="errorMessage" class="text-red-500 font-sans mt-4">
                    {{ errorMessage }}
                </p>
            </div>
        </div>

        <!-- Confirmation Dialog -->
        <div v-if="conformationPopupIsOpen" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div class="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                <h3 class="text-lg font-semibold mb-4">Confirm Deletion</h3>
                <p class="text-gray-600 mb-6">Are you sure you want to delete this outcome?</p>
                <div class="flex justify-end gap-4">
                    <button
                        @click="onCancelRemove"
                        class="px-4 py-2 text-gray-600 hover:text-gray-800"
                    >
                        Cancel
                    </button>
                    <button
                        @click="onConformRemove"
                        class="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                    >
                        Delete
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, nextTick } from 'vue';
import { useAuthStore } from '@/stores/auth';
import global from '@/global';
import { useStepValidation } from '@/composables/useStepValidation';

const outcomeState = ref({
  outcomes: [],
});

const isOutcomesValid = ref({
  isValid: true,
  issue: null,
});

const isLoading = ref(true);
const errorMessage = ref('');
const duplicateErrorMessage = ref('Duplicate theme name is not allowed!');
const conformationPopupIsOpen = ref(false);
const conformDeletionState = ref(null);
const showValidationError = ref(false);
const validationMessageRef = ref(null);

const authStore = useAuthStore();

const syncOutcomesCache = (outcomes) => {
  localStorage.setItem('outcomes', JSON.stringify(outcomes));
  window.dispatchEvent(new CustomEvent('outcomes-updated', {
    detail: { outcomes },
  }));
};

const countWords = (value) => {
  if (!value) return 0;
  return value.trim().split(/\s+/).filter((word) => word.length > 0).length;
};

const normalizeOutcomeTitle = (value) => value?.trim().toLowerCase() ?? "";

const scrollToValidationMessage = async () => {
  await nextTick();
  validationMessageRef.value?.scrollIntoView({
    behavior: 'smooth',
    block: 'center',
  });
};

const onAddOutcome = () => {
  if (outcomeState.value.outcomes.length < 5) {
    outcomeState.value.outcomes.push({
      id: null,
      title: '',
      description: '',
      wordCount: 0,
    });
    showValidationError.value = false;
  }
};

const onRemoveOutcome = (event) => {
  const idx = event.currentTarget.dataset.pointId;
  conformationPopupIsOpen.value = true;
  conformDeletionState.value = idx;
};

const onCancelRemove = () => {
  conformationPopupIsOpen.value = false;
  conformDeletionState.value = null;
};

const onConformRemove = async () => {
  if (conformDeletionState.value === null) return;

  const idx = Number(conformDeletionState.value);
  const outcomeToDelete = outcomeState.value.outcomes[idx];

  try {
    if (outcomeToDelete?.id) {
      const res = await fetch(`${global.API_URL}/outcomes/${outcomeToDelete.id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${authStore.user.accessToken}`,
        },
      });

      if (!res.ok) {
        throw new Error(`HTTP error! Status: ${res.status}`);
      }
    }

    outcomeState.value.outcomes.splice(idx, 1);
    syncOutcomesCache(outcomeState.value.outcomes);
    showValidationError.value = false;
    errorMessage.value = '';
  } catch (error) {
    console.error('Failed to delete outcome:', error);
    errorMessage.value = 'Failed to delete outcome. Please try again.';
  } finally {
    conformationPopupIsOpen.value = false;
    conformDeletionState.value = null;
  }
};

const onOutcomeThemeInput = () => {
  showValidationError.value = false;
};

const onOutcomeDescriptionInput = (event) => {
  const idx = event.currentTarget.dataset.outcomeIndex;
  const text = event.target.value;
  const words = text.trim().split(/\s+/).filter((word) => word.length > 0);
  outcomeState.value.outcomes[idx].wordCount = words.length;
  showValidationError.value = false;
};

const validateOutcomes = () => {
  errorMessage.value = '';
  duplicateErrorMessage.value = 'Duplicate theme name is not allowed!';

  if (outcomeState.value.outcomes.length === 0) {
    isOutcomesValid.value = {
      isValid: false,
      issue: 'empty',
    };
    showValidationError.value = true;
    scrollToValidationMessage();
    return;
  }

  const hasInvalidOutcome = outcomeState.value.outcomes.some((outcome) => !outcome.title?.trim()
        || !outcome.description?.trim()
        || outcome.wordCount > 85);

  if (hasInvalidOutcome) {
    isOutcomesValid.value = {
      isValid: false,
      issue: 'incomplete',
    };
    showValidationError.value = true;
    scrollToValidationMessage();
    return;
  }

  const uniqueTitles = new Set();
  const hasDuplicateTitle = outcomeState.value.outcomes.some((outcome) => {
    const normalizedTitle = normalizeOutcomeTitle(outcome.title);

    if (uniqueTitles.has(normalizedTitle)) {
      return true;
    }

    uniqueTitles.add(normalizedTitle);
    return false;
  });

  isOutcomesValid.value = {
    isValid: !hasDuplicateTitle,
    issue: hasDuplicateTitle ? 'duplicate' : null,
  };

  if (hasDuplicateTitle) {
    showValidationError.value = true;
    scrollToValidationMessage();
  }
};

async function fetchOutcomes() {
  try {
    const res = await fetch(`${global.API_URL}/outcomes`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${authStore.user.accessToken}`,
      },
    });

    if (!res.ok) {
      throw new Error(`HTTP error! Status: ${res.status}`);
    }

    const data = await res.json();
    const outcomes = Array.isArray(data.outcomes) ? data.outcomes : [];

    outcomeState.value.outcomes = outcomes.map((outcome) => ({
      id: outcome.id,
      title: outcome.title ?? '',
      description: outcome.description ?? '',
      wordCount: countWords(outcome.description ?? ''),
    }));
    syncOutcomesCache(outcomeState.value.outcomes);

    errorMessage.value = '';
    showValidationError.value = false;
  } catch (error) {
    console.error('Failed to fetch outcomes:', error);
    errorMessage.value = 'Failed to load outcomes. Please try again.';
  }
}
async function saveOutcomes() {
  try {
    const res = await fetch(`${global.API_URL}/outcomes`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${authStore.user.accessToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        outcomes: outcomeState.value.outcomes.map((outcome) => ({
          id: outcome.id,
          title: outcome.title,
          description: outcome.description,
        })),
      }),
    });

    const data = await res.json().catch(() => ({}));

    if (!res.ok) {
      if (['duplicate_outcome_title', 'duplicate_outcome_titles'].includes(data?.error_code)) {
        duplicateErrorMessage.value = 'Duplicate theme name is not allowed!';
        isOutcomesValid.value = {
          isValid: false,
          issue: 'duplicate',
        };
        showValidationError.value = true;
        errorMessage.value = '';
        scrollToValidationMessage();
        return false;
      }

      throw new Error(data?.error || `HTTP error! Status: ${res.status}`);
    }

    await fetchOutcomes();
    return true;
  } catch (error) {
    console.error('Failed to save outcomes:', error);
    errorMessage.value = 'Failed to save outcomes. Please try again.';
    return false;
  }
}
useStepValidation({
  validateFn: async () => {
    validateOutcomes();
    if (isOutcomesValid.value.isValid) {
      return saveOutcomes();
    }
    return false;
  },
});

onMounted(async () => {
  isLoading.value = true;
  await fetchOutcomes();
  isLoading.value = false;
});
</script>

<style scoped>
.answer-err-enter-active,
.answer-err-leave-active {
    transition: opacity 0.3s ease;
}

.answer-err-enter-from,
.answer-err-leave-to {
    opacity: 0;
}

.shakeX {
    animation: shakeX 0.5s cubic-bezier(.36,.07,.19,.97) both;
}

@keyframes shakeX {
    10%, 90% {
        transform: translate3d(-1px, 0, 0);
    }
    20%, 80% {
        transform: translate3d(2px, 0, 0);
    }
    30%, 50%, 70% {
        transform: translate3d(-4px, 0, 0);
    }
    40%, 60% {
        transform: translate3d(4px, 0, 0);
    }
}
</style>
